import axios from 'axios';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import { calculateEntryAmount, round2 } from '@/services/utils';
export function useLineItemActions() {
  const {
    handleError
  } = useSnackbarHandler();
  const makeNonBillableLineItem = lineItem => {
    lineItem.billingType = 'nonBillable';
    lineItem.amount = 0;
    lineItem.charges = null;
    lineItem.rate.amount = 0;
    lineItem.entries = lineItem.entries.map(e => ({
      ...e,
      billingType: 'nonBillable',
      rate: {
        label: '',
        amount: 0,
        edited: true
      },
      chargeId: null
    }));
    lineItem.status = 'updated';
  };
  const makeLineItemBillable = async lineItem => {
    lineItem.charges = null;
    lineItem.billingType = 'billable';
    for (var entry of lineItem.entries) {
      if (entry.billingType !== 'billable') {
        try {
          // Get the matter-specific rate for this user
          const response = await axios.get(`/matters/${entry.matter.id}/users/${entry.account.id}/rate`);
          if (response.data && response.data.rate && typeof response.data.rate.amount === 'number') {
            // API returned a valid rate - use it regardless of the amount (could be 0 if that's the configured rate)
            entry.rate = {
              ...response.data.rate
            };
            entry.rate.edited = true;
            lineItem.rate = entry.rate;
            lineItem.amount = calculateEntryAmount(lineItem.rate.amount, lineItem.hours);
          } else {
            // API response was invalid or missing rate data
            handleError(`Failed to retrieve rate for user "${entry.account.name}" on matter "${entry.matter.name}". This can happen if the timekeeper was removed from the matter. Please check the Billing and Rates for the matter.`);
            return;
          }
        } catch (error) {
          // Network or other API error - use original error message for more specific details
          handleError(`Error: ${error.message}. Please contact Support if this persists.`);
          return;
        }
      }
      entry.billedHours = entry.hours;
      entry.billingType = 'billable';
      entry.chargeId = null;
    }
    const totalHours = lineItem.hours;
    // If a rate is present, calculate the amount dynamically
    const rate = lineItem.rate?.amount || 0; // Use the existing rate or fallback to original
    const totalAmount = calculateEntryAmount(rate, totalHours);
    lineItem.amount = totalAmount;

    // Update rate dynamically if necessary
    const updatedRate = totalHours > 0 ? round2(lineItem.amount / totalHours) : rate;
    lineItem.rate.amount = updatedRate;
    lineItem.status = 'updated';
  };
  const writeDownLineItemToZero = lineItem => {
    lineItem.amount = 0;
    lineItem.hours = 0;
    lineItem.status = 'writeDown';
    lineItem.entries.forEach(entry => {
      entry.writedown = entry.hours;
      entry.billedHours = 0;
    });
  };

  // lineItem description functions
  const constructDescription = (lineItem, settings) => {
    let codes = '';
    let initials = '';
    let matterName = '';
    let description = '';
    if (settings.includeCodes) {
      codes = getCodes(lineItem);
    }
    if (settings.includeInitials && lineItem.account) {
      initials += (lineItem.account.initials || lineItem.account.name) + ': ';
    }
    if (settings.includeMatterName && settings.matter) {
      matterName += settings.matter.name + ': ';
    }
    description = getDescription(lineItem, settings);
    if (lineItem.charges) {
      lineItem.descriptionPreview = codes + matterName + description;
    } else if (lineItem.expenses) {
      lineItem.descriptionPreview = codes + initials + matterName + description;
    } else {
      lineItem.descriptionPreview = matterName + initials + codes + description;
    }
    return lineItem.descriptionPreview;
  };
  const getDescription = (lineItem, settings) => {
    let description = '';
    if (lineItem.expenses) {
      description += lineItem.expenses[0].description;
    } else if (lineItem.entries) {
      let separator = lineItem.entries.length > 1 ? '; ' : ' ';
      lineItem.entries.forEach(entry => {
        description += entry.description + (settings.includeHours ? ' (' + entry.hours + ') ' : '') + separator;
      });
    } else if (lineItem.charges) {
      description += lineItem.charges[0].description;
    }
    return description;
  };
  const getCodes = lineItem => {
    let codes = '';
    if (lineItem.expenses) {
      let codeId = lineItem.expenses[0].codeId || lineItem.expenses[0].code.id;
      codes += codeId + ': ';
    } else if (lineItem.entries) {
      var activityCodeId = lineItem.codes?.activity?.id || lineItem.entries[0].codes?.activity?.id;
      var taskCodeId = lineItem.codes?.task?.id || lineItem.entries[0].codes?.task?.id;
      var codeId = activityCodeId ? activityCodeId : '';
      if (activityCodeId && taskCodeId) {
        codeId += ' ' + taskCodeId;
      } else if (taskCodeId) {
        codeId += taskCodeId;
      }
      if (codeId.length > 0) {
        codes += codeId + ': ';
      }
    } else if (lineItem.charges) {
      var activityCodeId = lineItem.codes?.activity?.id || lineItem.charges[0].codes?.activity?.id;
      var taskCodeId = lineItem.codes?.task?.id || lineItem.charges[0].codes?.task?.id;
      var codeId = activityCodeId ? activityCodeId : '';
      if (activityCodeId && taskCodeId) {
        codeId += ' ' + taskCodeId;
      } else if (taskCodeId) {
        codeId += taskCodeId;
      }
      if (codeId.length > 0) {
        codes += codeId + ': ';
      }
    }
    return codes;
  };

  // end lineItem description functions

  return {
    makeNonBillableLineItem,
    makeLineItemBillable,
    writeDownLineItemToZero,
    constructDescription
  };
}